/**
 * Detroit Bankruptcy Solo マップ設定
 * Detroit Bankruptcy Solo 専用の設定値を定義
 */

import type { GoodsCubesConfig, GoodsDisplayConfig, InitialPlacementConfig } from '../../../src/game/utils/goodsSetup';
import type { MapConfig, TileType } from '../../../src/types';

// ============================================================================
// マップ基本設定
// ============================================================================

/**
 * Detroit Bankruptcy Solo マップ設定
 */
export const DETROIT_MAP_CONFIG: MapConfig = {
  name: 'Detroit Bankruptcy Solo',
  // Common ルール: A-H すべて配置可能（Barbados のみ A-D 制限）
  availableNewCityTiles: ['NEW_CITY_A', 'NEW_CITY_B', 'NEW_CITY_C', 'NEW_CITY_D', 'NEW_CITY_E', 'NEW_CITY_F', 'NEW_CITY_G', 'NEW_CITY_H'] as TileType[],
  defaultPlayerCount: 1,
  maxTurns: -1,  // 無制限（破産まで継続）
  hasGoodsDisplay: true,
  hasEngineerBonus: true,  // エンジニア選択時、最安タイルを無料
  expenseFormula: 'with_turn',  // 株式 + 機関車Lv + ターン数
  incomeReductionFormula: 'detroit',  // 5ステップごと
  issueSharesHelpKey: 'detroit',
  isSoloPlay: true,
  maxSharesLimit: 25,  // Detroit固有: 株式発行上限25枚
};

// ============================================================================
// 商品設定
// ============================================================================

/**
 * Detroit 商品キューブ設定
 * - 全5色使用
 * - 各色標準数
 */
export const DETROIT_CUBES_CONFIG: GoodsCubesConfig = {
  cubesPerColor: {
    red: 20,
    blue: 20,
    purple: 20,
    yellow: 20,
    black: 20
  },
  excludeColors: []  // 全色使用
};

/**
 * Detroit 商品補充表設定
 * - 両側（明/暗）使用
 * - 最上段に計14個配置（都市列6個×2 + 新都市列2個）
 */
export const DETROIT_DISPLAY_CONFIG: GoodsDisplayConfig = {
  lightSideInitialCount: 7,  // 明側: 都市6列 + 新都市1列（ただし実際は明側全列分）
  darkSideEnabled: true,     // 暗側も使用
  lightSideCityIds: [
    'YPSILANTI',       // 列1 (white)
    'NOVI',            // 列2 (white)
    'MILFORD',         // 列3 (white)
    'PONTIAC',         // 列4 (white)
    'UTICA',           // 列5 (white)
    'WINDSOR'          // 列6 (white)
  ],
  lightSideNewCities: [
    { columnNumber: 3, newCityId: 'A' },
    { columnNumber: 4, newCityId: 'B' },
    { columnNumber: 5, newCityId: 'C' },
    { columnNumber: 6, newCityId: 'D' }
  ],
  darkSideNewCities: [
    { columnNumber: 1, newCityId: 'E' },
    { columnNumber: 2, newCityId: 'F' },
    { columnNumber: 3, newCityId: 'G' },
    { columnNumber: 4, newCityId: 'H' }
  ],
  // Detroit uses dark side city IDs
  darkSideCityIds: [
    'DETROIT_METRO',      // 列1 (black)
    'DEARBORN',           // 列2 (black)
    'SOUTHFIELD',         // 列3 (black)
    'ST._CLAIR_SHORES',   // 列4 (black)
    'MIDTOWN_DETROIT',    // 列5 (black)
    'DOWNTOWN_DETROIT'    // 列6 (black)
  ]
};

/**
 * Detroit 初期配置設定
 * - 各都市に2個ずつ配置
 */
export const DETROIT_PLACEMENT_CONFIG: InitialPlacementConfig = {
  targetCityColor: null,  // 全都市に配置
  cubesPerCity: 2
};

// ============================================================================
// ゲーム特有ルール
// ============================================================================

/**
 * Detroit Bankruptcy Solo 固有ルール
 */
export const DETROIT_RULES = {
  /** 初期資金 */
  initialCash: 0,
  /** 初期株式数 */
  initialShares: 5,
  /** 初期機関車レベル */
  initialLocoLevel: 1,
  /** 株式発行上限 */
  maxSharesLimit: 25,
  /** アクションディスク初期数（各アクション） */
  initialDisksPerAction: 3,
  /** 勝利ターン数（このターン以上で破産したら勝利） */
  victoryTurnThreshold: 7,
  /** ソロプレイ専用 */
  soloPlayOnly: true
} as const;

// ============================================================================
// UI設定
// ============================================================================

/**
 * Detroit GameStartModal 設定
 *
 * 共通キーは gameStartCommon から取得、マップ固有キーは gameStartDetroit から取得。
 */
export const DETROIT_START_MODAL_CONFIG = {
  /** i18n マップ固有キープレフィックス */
  i18nKey: 'gameStartDetroit',
  /** 勝利条件セクションのスタイル */
  victoryStyle: 'success' as const,
  /** 勝利/終了条件設定（統一構造） */
  victory: {
    gameEndKey: 'gameEnd',  // 共通キー
    winConditionKey: 'winCondition',  // マップ固有キー
    additionalItems: ['scoreIsTurns'],  // マップ固有キー
  },
  /** 特殊ルールフェーズ */
  phases: [
    { num: '1', ruleKey: 'issueSharesRule', descKey: 'issueSharesDesc' },
    { num: '3', ruleKey: 'selectActionsRule', descKey: 'selectActionsDesc', actionsKey: 'selectActionsActions', extraKeys: ['diskSystem', 'diskFee'] },
    { num: '7', descKey: 'payExpensesDesc' },
    { num: '8', descKey: 'incomeReductionDesc' },
    { num: '9', descKey: 'refillGoodsDesc' },
  ],
  /** 戦略ヒント */
  tips: ['tip1', 'tip2', 'tip3', 'tip4'],
  /** 特殊リンク */
  specialLinks: ['ferryLinkDesc', 'adjustLincDesc'],
};

/**
 * Detroit GameEndScreen 設定
 */
export const DETROIT_END_SCREEN_CONFIG = {
  /** スコア計算タイプ */
  scoreType: 'turns' as const,  // 'cash' | 'income' | 'turns'
  /** 勝利判定（7ターン以上で破産したら勝利） */
  victoryThreshold: 7,
  /** スコア評価（降順でマッチング） */
  scoreEvaluations: [
    { minScore: 12, key: 'turn12' },
    { minScore: 11, key: 'turn11' },
    { minScore: 10, key: 'turn10' },
    { minScore: 9, key: 'turn9' },
    { minScore: 8, key: 'turn8' },
    { minScore: 7, key: 'turn7' },
    { minScore: 0, key: 'defeat' },
  ],
};
